import { NgClass } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';

import { MatIconModule } from '@angular/material/icon';
import {
  MatPaginator,
  MatPaginatorIntl,
  MatPaginatorModule,
} from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import {
  MatTable,
  MatTableDataSource,
  MatTableModule,
} from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { BoolTreeNode } from 'src/app/shared/classes/bool-tree-node';
import * as Orders from '../../../domain';
import { OrderPaginatorService } from './order-paginator.service';
import OrderRow from './order-row.interface';
import { ExtendedRowComponent } from './extended-row/extended-row.component';

const MAX_PAGE_SIZE_LABEL = 'All';
const MAX_PAGE_SIZE = 99999;

@Component({
  selector: 'app-table-orders',
  templateUrl: './order-table.component.html',
  encapsulation: ViewEncapsulation.None,
  providers: [{ provide: MatPaginatorIntl, useClass: OrderPaginatorService }],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgClass,
    MatProgressSpinnerModule,
    MatIconModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    ExtendedRowComponent,
  ],
})
export class OrderTableComponent implements OnInit, OnChanges {
  @Input() data: OrderRow[] = [];

  @Input() loading: boolean = true;

  @Input() set externalQuery(q: string) {
    if (q !== this.query) {
      this.query = q;
      this.triggerSearch();
    }
  }

  @Input() filterService: readonly BoolTreeNode<{
    label: string;
    value: string;
  }>[];

  @Input() filterStatus: readonly BoolTreeNode<{
    label: string;
    value: Orders.Status;
  }>[];

  @Input() filterAddress: readonly BoolTreeNode<{
    label: string;
    value: string;
  }>[];

  @Output() filterRemove = new EventEmitter<any>();

  @Output() selectOrder = new EventEmitter<Orders.Order>();

  @ViewChild(MatTable) table: MatTable<OrderRow>;

  private paginatorRef: MatPaginator;

  @ViewChild(MatPaginator) set paginator(value: MatPaginator) {
    this.paginatorRef = value;
    if (this.dataSource && value) {
      this.dataSource.paginator = value;
    }
  }

  get paginator(): MatPaginator {
    return this.paginatorRef;
  }

  @ViewChild('paginatorElement', { read: ElementRef })
  paginatorHtmlElement: ElementRef;

  @ViewChild(MatSort) sort: MatSort;

  public orderColumns: string[] = [
    //		'id',
    //		'oli',
    'nit',
    'deal',
    //		'site',
    'address',
    'city',
    'state',
    'service',
    'line_type',
    'status',
    'foc_date',
    'expands',
  ];

  public allExpanded: boolean = false;

  public dataSource: MatTableDataSource<OrderRow>;

  public pageSizeOptions: any[];

  public csvDownloadFileName: string = 'Orders.csv';

  public query: string = '';

  public searchLoading: boolean = false;

  private searchSubject: Subject<string> = new Subject();

  private firstRun: boolean = true;

  private cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.loadDataSource();
    this.startSearchFn();
  }

  ngOnChanges(): void {
    if (this.firstRun) {
      this.firstRun = false;
      this.cdr.markForCheck();
      return;
    }
    this.refresh();
  }

  removeThis(type, item): void {
    this.filterRemove.emit({ type, item });
  }

  private startSearchFn(): void {
    this.searchSubject
      .pipe(debounceTime(500))
      .subscribe((query) => this.searchData(query));
  }

  private searchData(query: string): void {
    if (!query) return this.loadDataSource();
    query = query.toLowerCase();
    const filteredData = this.data.filter(
      (row) =>
        row.nit.toLowerCase().includes(query) ||
        row.deal?.toLowerCase().includes(query) ||
        row.site?.toLowerCase().includes(query) ||
        row.address?.toLowerCase().includes(query) ||
        row.city?.toLowerCase().includes(query) ||
        row.state?.toLowerCase().includes(query) ||
        row.service?.toLowerCase().includes(query) ||
        row.line_type?.toLowerCase().includes(query) ||
        row.status?.toLowerCase().includes(query) ||
        row.foc_date?.toLowerCase().includes(query),
    );
    this.loadDataSource(filteredData);
  }

  public triggerSearch(): void {
    this.searchLoading = true;
    this.searchSubject.next(this.query);
    this.cdr.markForCheck();
  }

  /**
   * Sets the select value to 'All' for the mat-paginator when the page size is {MAX_PAGE_SIZE}
   */
  public setSelectValueToAll(): void {
    const pageSize = this.paginator?.pageSize;
    const pageLabel =
      pageSize === MAX_PAGE_SIZE ? MAX_PAGE_SIZE_LABEL : pageSize.toString();

    const DISPLAY_QUERY_SELECTOR =
      '.mat-mdc-select-value-text > .mat-mdc-select-min-line';
    const paginatorSelect = this.paginatorHtmlElement.nativeElement;

    const element = paginatorSelect.querySelector(DISPLAY_QUERY_SELECTOR);
    element.innerHTML = pageLabel;
  }

  /**
   * Sets the option label to 'All' for the page size option with {MAX_PAGE_SIZE} value
   */
  public setOptionLabelToAll(): void {
    const OPTION_QUERY_SELECTOR =
      'mat-option.mat-mdc-option.mdc-list-item > .mdc-list-item__primary-text';
    const elements = document.querySelectorAll<HTMLElement>(
      OPTION_QUERY_SELECTOR,
    );
    const match: HTMLElement | undefined = Array.from(elements).find(
      (el): el is HTMLElement => el.innerText === String(MAX_PAGE_SIZE),
    );

    if (match != null) {
      match.innerText = MAX_PAGE_SIZE_LABEL;
    }
  }

  public toggleExpandAll(): void {
    this.allExpanded = !this.allExpanded;
    this.data.forEach((r) => {
      r.expanded = this.allExpanded;
    });
    this.loadDataSource();
  }

  private startPageSizeOptions() {
    if (!this.data) return;
    const max = this.data.length;
    this.pageSizeOptions = [];
    if (max < 50) {
      this.pageSizeOptions.push(10);
    }
    this.pageSizeOptions.push(50);
    if (max > 100) {
      this.pageSizeOptions.push(100);
    }
    this.pageSizeOptions.push(MAX_PAGE_SIZE);
  }

  private loadDataSource(useData: OrderRow[] = []) {
    this.startPageSizeOptions();
    const orderData = useData.length ? useData : this.data;
    this.dataSource = new MatTableDataSource<OrderRow>(orderData);
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    if (this.table) this.table.renderRows();
    this.searchLoading = false;
  }

  refresh(): void {
    this.loadDataSource();
    if (!this.table) return;
    this.table.renderRows();
  }

  openOrder(order: Orders.Order): void {
    this.selectOrder.emit(order);
  }

  getOrderCsvData(): Orders.OrderCsvDataRow[] {
    if (!this.data.length) return [];
    return Orders.getOrderCSVData(this.data);
  }
}
